import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FiFileText,
  FiTrash2,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiZap,
  FiMapPin,
  FiPlay,
  FiExternalLink,
  FiEye,
  FiRefreshCw,
  FiLink,
  FiChevronDown,
  FiChevronRight,
  FiWifi,
  FiWifiOff,
  FiClock,
  FiX,
  FiShield,
  FiEdit3,
  FiList,
  FiUploadCloud,
} from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { lireReponseApi } from '../../utils/reponseApiTurpe';
import { versionPourSauvegarde } from '../../utils/resumeTurpe';
import { ouvrirDocumentStocke } from '../../utils/documentStocke';

// ============================================================================
// CONSTANTS
// ============================================================================

const ACHEMINEMENT_DOCS = [
  { id: 'infos_techniques_contractuelles', name: 'Infos Techniques (C68)', required: true },
  { id: 'courbe_charge', name: 'Courbe de charge (R63)', required: true },
  { id: 'historique_consommation', name: 'Historique de consommation (R67)', required: true },
  { id: 'donnees_techniques_soutirage', name: 'Données techniques de soutirage', required: false },
];

// ============================================================================
// UTILITIES
// ============================================================================

/** Format timestamptz to "dd/mm/yyyy HH:mm" in Europe/Paris */
function formatDateParis(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('fr-FR', {
      timeZone: 'Europe/Paris',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(ts).slice(0, 16);
  }
}

/** Map enedis connection status to badge props */
function mapStatusToBadge(status) {
  switch (status) {
    case 'CONNECTED':
      return { label: 'Connecte', color: 'bg-green-100 text-green-800', icon: FiWifi };
    case 'PENDING':
      return { label: 'En cours...', color: 'bg-yellow-100 text-yellow-800', icon: FiClock };
    case 'ERROR':
      return { label: 'Erreur', color: 'bg-red-100 text-red-800', icon: FiAlertCircle };
    case 'NOT_CONNECTED':
    default:
      return { label: 'Non connecte', color: 'bg-gray-100 text-gray-600', icon: FiWifiOff };
  }
}

/** Map SFTP file status to badge */
function mapSftpStatusBadge(status) {
  switch (status) {
    case 'PARSED': return { label: 'Traite', cls: 'bg-green-100 text-green-800' };
    case 'STORED': return { label: 'En attente', cls: 'bg-yellow-100 text-yellow-800' };
    case 'PARSE_FAILED': return { label: 'Echec', cls: 'bg-red-100 text-red-800' };
    case 'FAILED': return { label: 'Echec', cls: 'bg-red-100 text-red-800' };
    default: return { label: status || '—', cls: 'bg-gray-100 text-gray-600' };
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

const AcheminementDetail = ({ clientData, session, analysisResults = {}, onRefresh }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const targetPdlNumber = searchParams.get('pdl');
  const targetAction = searchParams.get('action');
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current || !targetPdlNumber) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-pdl="${targetPdlNumber}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-emerald-500', 'ring-offset-2', 'transition');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-emerald-500', 'ring-offset-2', 'transition');
      }, 2000);

      // Pas d'auto-click pour les actions side-effecty (connect, sign-mandat) :
      // l'utilisateur reste maître du déclenchement (notamment de la sync ENEDIS
      // qui appelle l'API et engage des effets externes).
      // Seul `optimize` est auto-déclenché car c'est l'action finale explicitement
      // demandée et idempotente côté analyse.
      if (targetAction === 'optimize') {
        const findCta = (selector) => el.querySelector(selector) || document.querySelector(selector);
        findCta('[data-cta="optimize"]')?.click();
      }

      const next = new URLSearchParams(searchParams);
      next.delete('pdl');
      next.delete('action');
      setSearchParams(next, { replace: true });
      consumed.current = true;
    }, 100);
    return () => clearTimeout(t);
  }, [targetPdlNumber, targetAction, searchParams, setSearchParams]);

  // ---------- IDs ----------
  // userId = identifiant requis par les API Python TURPE (v3/v4). Historiquement
  // c'était une colonne pdl.user_id, depuis supprimée → on utilise l'owner_id du
  // compte (passé par ClientDetailPage en `ownerUserId` + `user_id`).
  const userId = clientData?.user_id || clientData?.userId || clientData?.ownerUserId;
  const accountId = useMemo(() => {
    return (
      clientData?.account_id ||
      clientData?.account?.id ||
      clientData?.account?.account_id ||
      clientData?.accountId ||
      null
    );
  }, [clientData]);

  // ---------- STATES ----------
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedPointId, setSelectedPointId] = useState(null);
  const [selectedPointType, setSelectedPointType] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  const [pdls, setPdls] = useState(clientData?.pdls || []);
  const [pces, setPces] = useState(clientData?.pces || []);

  const [optimizingStates, setOptimizingStates] = useState({});
  const fiscalYearOptions = [2023, 2024, 2025, 2026, 2027];

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadingCourbeCharge, setIsUploadingCourbeCharge] = useState(false);
  const [isUploadingHistorique, setIsUploadingHistorique] = useState(false);
  const [isUploadingInfosTechniques, setIsUploadingInfosTechniques] = useState(false);
  const [isUploadingSoutirage, setIsUploadingSoutirage] = useState(false);

  const [isRefreshing, setIsRefreshing] = useState(false);

  // --- NEW: Synchro Enedis states ---
  const [mandateOk, setMandateOk] = useState(false);
  const [mandateSignedAt, setMandateSignedAt] = useState(null);
  const [mandateDocumentId, setMandateDocumentId] = useState(null);
  const [enedisStatus, setEnedisStatus] = useState('NOT_CONNECTED'); // per selected PDL
  const [isConnecting, setIsConnecting] = useState(false);
  const [fallbackOpen, setFallbackOpen] = useState(false);

  // ZIP multi-upload modal (mode secours)
  const [showZipModal, setShowZipModal] = useState(false);
  const [zipModalPoint, setZipModalPoint] = useState(null);
  const [zipImporting, setZipImporting] = useState(false);
  const zipInputRef = useRef(null);

  // --- NEW: SFTP detail drawer ---
  const [showSftpDrawer, setShowSftpDrawer] = useState(false);
  const [sftpDrawerPdlId, setSftpDrawerPdlId] = useState(null);
  const [sftpFiles, setSftpFiles] = useState([]);
  const [sftpLoading, setSftpLoading] = useState(false);

  // --- Legacy mandat state (for URL view) ---
  const [signedMandat, setSignedMandat] = useState(null);
  const [signedMandatPublicUrl, setSignedMandatPublicUrl] = useState(null);

  // Polling ref
  const pollRef = useRef(null);

  // ---------- HELPERS ----------
  const authHeaders = useMemo(() => {
    if (!session?.access_token) return {};
    return { Authorization: 'Bearer ' + session.access_token };
  }, [session?.access_token]);

  // ---------- DELIVERY POINTS ----------
  const loadDeliveryPoints = useCallback(async () => {
    if (!accountId || !session?.access_token) return;
    try {
      const [pdlsRes, pcesRes] = await Promise.allSettled([
        axios.get('/api/pdl?accountId=' + accountId, { headers: authHeaders }),
        axios.get('/api/pce?accountId=' + accountId, { headers: authHeaders }),
      ]);
      if (pdlsRes.status === 'fulfilled') setPdls(pdlsRes.value?.data?.data || []);
      if (pcesRes.status === 'fulfilled') setPces(pcesRes.value?.data?.data || []);
    } catch (e) {
      console.warn('[AcheminementDetail] loadDeliveryPoints:', e?.message);
    }
  }, [accountId, session?.access_token, authHeaders]);

  const deliveryPoints = useMemo(() => {
    const pdlItems = (pdls || []).map(p => ({
      ...p, type: 'pdl', point_id: p.id,
      point_number: p.pdl_number,
      site_name: p.site_name || p.name || 'Site Inconnu',
    }));
    const pceItems = (pces || []).map(p => ({
      ...p, type: 'pce', point_id: p.id,
      point_number: p.pce_number || p.pce,
      site_name: p.site_name || p.name || 'Site Inconnu',
    }));
    return [...pdlItems, ...pceItems].sort((a, b) => {
      if (a.type !== b.type) return a.type === 'pdl' ? -1 : 1;
      return String(a.site_name || '').localeCompare(String(b.site_name || ''), 'fr');
    });
  }, [pdls, pces]);

  // ---------- MANDATE CHECK (account-level) ----------
  // Gating: type='mandate', status='signed', is_manual_upload=false, signed_at non null
  // PDL délégués : si le compte n'a pas de mandat direct mais qu'au moins un de
  // ses PDL a un délégataire qui a un mandat signé, on considère mandateOk = true
  // (cas ASSOC SYND COUZE CHAMBON / SOMIVAL — Caroline 29/06).
  const loadMandateStatus = useCallback(async () => {
    if (!accountId || !session?.access_token) {
      setMandateOk(false);
      setSignedMandat(null);
      return;
    }

    const isValidMandate = (d) =>
      (d?.status || '').toLowerCase() === 'signed' &&
      d?.is_manual_upload !== true &&
      d?.signed_at != null;

    try {
      // 1. Mandat sur le compte direct
      const directResp = await axios.get(
        '/api/documents?type=mandate&account_id=' + accountId,
        { headers: authHeaders }
      );
      const directRows = directResp.data?.data || [];
      const validDirect = directRows.find(isValidMandate);

      if (validDirect) {
        setMandateOk(true);
        setMandateSignedAt(validDirect.signed_at);
        setMandateDocumentId(validDirect.id);
        setSignedMandat(validDirect);
        return;
      }

      // 2. Fallback délégataires : récupérer les PDL du compte, extraire les
      // delegataire_account_id uniques, fetcher leur mandat.
      let delegataireIds = [];
      try {
        const pdlResp = await axios.get('/api/pdl?accountId=' + accountId, { headers: authHeaders });
        const pdlRows = pdlResp.data?.data || [];
        delegataireIds = [...new Set(pdlRows.map(p => p.delegataire_account_id).filter(Boolean))];
      } catch (e) {
        console.warn('[AcheminementDetail] loadMandateStatus pdl fetch:', e?.message);
      }

      for (const delegId of delegataireIds) {
        try {
          const delegResp = await axios.get(
            '/api/documents?type=mandate&account_id=' + delegId,
            { headers: authHeaders }
          );
          const validDeleg = (delegResp.data?.data || []).find(isValidMandate);
          if (validDeleg) {
            setMandateOk(true);
            setMandateSignedAt(validDeleg.signed_at);
            setMandateDocumentId(validDeleg.id);
            setSignedMandat(validDeleg);
            return;
          }
        } catch (e) {
          console.warn('[AcheminementDetail] mandat délégataire ' + delegId + ':', e?.message);
        }
      }

      // 3. Aucun mandat valide trouvé (ni direct ni via délégataire)
      setMandateOk(false);
      setMandateSignedAt(null);
      setMandateDocumentId(null);
      setSignedMandat(directRows.find(d => (d?.status || '').toLowerCase() === 'signed') || directRows[0] || null);
    } catch (e) {
      console.warn('[AcheminementDetail] loadMandateStatus:', e?.message);
      setMandateOk(false);
      setSignedMandat(null);
    }
  }, [accountId, session?.access_token, authHeaders]);

  // ---------- MANDAT PUBLIC URL (read-only) ----------
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (signedMandat?.signed_document_url) {
        setSignedMandatPublicUrl(signedMandat.signed_document_url);
        return;
      }
      if (!signedMandat?.id || !session?.access_token) {
        setSignedMandatPublicUrl(null);
        return;
      }
      try {
        const resp = await axios.post(
          '/api/documents/' + signedMandat.id + '/signed-url',
          {},
          { headers: authHeaders }
        );
        if (!cancelled) setSignedMandatPublicUrl(resp.data?.signedUrl || null);
      } catch {
        if (!cancelled) setSignedMandatPublicUrl(null);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [signedMandat?.id, signedMandat?.signed_document_url, session?.access_token, authHeaders]);

  // ---------- DOCUMENTS ----------
  const loadAcheminementData = useCallback(async () => {
    // Le backend accepte indifféremment owner_id (auth user_id), account_id, ou
    // pdl.user_id legacy. Priorité au premier disponible pour ne pas bloquer.
    const resolveId = userId || accountId;
    if (!resolveId || !session?.access_token) {
      console.warn('[AcheminementDetail] loadAcheminementData: aucun ID résolu (userId/accountId manquants)');
      return;
    }
    try {
      const { data } = await axios.get('/api/enedis/documents/client/' + resolveId, { headers: authHeaders });
      setDocuments(data?.documents || []);
    } catch (e) {
      console.error('[AcheminementDetail] loadAcheminementData error:', e?.response?.status, e?.response?.data);
      toast.error('Impossible de charger les documents.');
    }
  }, [userId, accountId, session?.access_token, authHeaders]);

  // ---------- SFTP STATUS (via Node endpoint, NOT Supabase direct) ----------
  const loadSftpStatus = useCallback(async (pdlId) => {
    if (!pdlId || !session?.access_token) return;
    setSftpLoading(true);
    try {
      const resp = await axios.get('/api/enedis/sftp-status?pdlId=' + pdlId, { headers: authHeaders });
      setSftpFiles(resp.data?.files || []);
    } catch (e) {
      console.warn('[AcheminementDetail] loadSftpStatus:', e?.message);
      setSftpFiles([]);
    } finally {
      setSftpLoading(false);
    }
  }, [session?.access_token, authHeaders]);

  // ---------- ENEDIS CONNECTION HANDLERS ----------
  const handleConnectEnedis = useCallback(async (pdlId) => {
    if (!mandateOk) {
      toast.error('Mandat requis (signature electronique) avant de connecter.');
      return;
    }
    setIsConnecting(true);
    const toastId = toast.loading('Connexion a Enedis en cours...');
    try {
      await axios.post('/api/enedis/pdl/' + pdlId + '/connect', {}, { headers: authHeaders });
      setEnedisStatus('PENDING');
      toast.success('Demande envoyee. Synchronisation en cours...', { id: toastId });

      // Start polling every 20s
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const resp = await axios.get('/api/enedis/pdl/' + pdlId + '/connection-status', { headers: authHeaders });
          const newStatus = resp.data?.status || 'PENDING';
          setEnedisStatus(newStatus);
          if (newStatus === 'CONNECTED' || newStatus === 'ERROR') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (newStatus === 'CONNECTED') {
              toast.success('Connexion Enedis reussie !');
              await loadAcheminementData();
            } else {
              toast.error('Erreur de connexion Enedis.');
            }
          }
        } catch {
          // silent
        }
      }, 20000);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur de connexion.', { id: toastId });
      setEnedisStatus('ERROR');
    } finally {
      setIsConnecting(false);
    }
  }, [mandateOk, authHeaders, loadAcheminementData]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // ---------- SFTP DRAWER ----------
  const openSftpDrawer = useCallback((pdlId) => {
    setSftpDrawerPdlId(pdlId);
    setShowSftpDrawer(true);
    loadSftpStatus(pdlId);
  }, [loadSftpStatus]);

  // ---------- GUARDS ----------
  const hasMandat = mandateOk; // strict: signed + non-manual + signed_at

  const requireMandat = useCallback((actionLabel = 'Action') => {
    if (hasMandat) return true;
    toast.error('Mandat ENEDIS manquant — signez le mandat pour poursuivre.');
    return false;
  }, [hasMandat]);

  const openUploadModal = useCallback((pointId, pointType, year) => {
    if (!requireMandat('Upload documents')) return;
    setSelectedPointId(pointId);
    setSelectedPointType(pointType);
    setSelectedYear(year);
    setShowUploadModal(true);
  }, [requireMandat]);

  // ---------- REFRESH ----------
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    const toastId = toast.loading('Actualisation des donnees...');
    try {
      if (onRefresh) {
        await onRefresh();
      } else {
        await Promise.all([
          loadDeliveryPoints(),
          loadAcheminementData(),
          loadMandateStatus(),
        ]);
      }
      toast.success('Donnees actualisees.', { id: toastId });
    } catch {
      toast.error("L'actualisation a echoue.", { id: toastId });
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh, loadDeliveryPoints, loadAcheminementData, loadMandateStatus]);

  // ---------- INITIAL LOAD ----------
  useEffect(() => {
    const initialLoad = async () => {
      setIsLoading(true);
      await Promise.all([
        loadDeliveryPoints(),
        loadAcheminementData(),
        loadMandateStatus(),
      ]);
      setIsLoading(false);
    };
    initialLoad();
  }, [loadDeliveryPoints, loadAcheminementData, loadMandateStatus]);

  // ---------- UPLOAD HANDLERS (unchanged logic, use auth headers) ----------
  const handleCourbeChargeUpload = async (file, pdlId, year) => {
    if (!requireMandat('Courbe de charge')) return;
    setIsUploadingCourbeCharge(true);
    setUploadProgress(0);
    const toastId = toast.loading('Envoi du fichier ZIP...');
    const filePath = 'acheminement/' + session.user.id + '/' + pdlId + '/' + Date.now() + '_' + file.name;
    try {
      const { error: uploadError } = await supabase.storage
        .from('enedis-documents')
        .upload(filePath, file, {
          upsert: false,
          onUploadProgress: (pe) => setUploadProgress(Math.round((pe.loaded * 100) / pe.total)),
        });
      if (uploadError) throw uploadError;
      toast.loading('Fichier envoye. Demarrage du traitement...', { id: toastId });
      await axios.post('/api/enedis/trigger-courbe-charge-processing', {
        pdl_id: pdlId, storage_path: filePath,
        original_file_name: file.name, original_file_size: file.size, year,
      }, { headers: authHeaders });
      toast.success('Traitement demarre. Vous serez notifie.', { id: toastId });
      await loadAcheminementData();
    } catch (error) {
      toast.error('Erreur : ' + (error.response?.data?.message || error.message), { id: toastId });
    } finally {
      setIsUploadingCourbeCharge(false);
      setUploadProgress(0);
    }
  };

  const handleHistoriqueConsommationsUpload = async (file, pdlId, year) => {
    if (!requireMandat('Historique conso')) return;
    setIsUploadingHistorique(true);
    const toastId = toast.loading("Envoi et traitement de l'historique...");
    try {
      const form = new FormData();
      form.append('historiqueConsommationsFile', file);
      form.append('pdl_id', pdlId);
      form.append('year', year);
      await axios.post('/api/enedis/upload-historique-consommations', form, { headers: authHeaders });
      toast.success('Historique traite avec succes !', { id: toastId });
      await loadAcheminementData();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors du traitement.', { id: toastId });
    } finally {
      setIsUploadingHistorique(false);
    }
  };

  const handleInfosTechniquesUpload = async (file, pdlId, year) => {
    if (!requireMandat('Infos techniques')) return;
    setIsUploadingInfosTechniques(true);
    const toastId = toast.loading('Envoi des infos techniques...');
    try {
      const form = new FormData();
      form.append('infosTechniquesFile', file);
      form.append('pdl_id', pdlId);
      form.append('year', year);
      await axios.post('/api/enedis/upload-infos-techniques', form, { headers: authHeaders });
      toast.success('Infos techniques mises a jour !', { id: toastId });
      await loadAcheminementData();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erreur lors du traitement.', { id: toastId });
    } finally {
      setIsUploadingInfosTechniques(false);
    }
  };

  const handleSoutirageUpload = async (file, pdlId, year) => {
    if (!requireMandat('Soutirage')) return;
    setIsUploadingSoutirage(true);
    const toastId = toast.loading('Envoi des donnees de soutirage...');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('pdl_id', pdlId);
      form.append('year', year);
      await axios.post('/api/acheminement/upload-soutirage', form, { headers: authHeaders });
      toast.success('Donnees de soutirage mises a jour !', { id: toastId });
      await loadAcheminementData();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors du traitement.', { id: toastId });
    } finally {
      setIsUploadingSoutirage(false);
    }
  };

  // ---------- ANALYSIS LAUNCH (PDL only) ----------
  const handleLaunchAnalysis = useCallback(async (pointId, yearToAnalyze, pointType = 'pdl') => {
    if (!requireMandat('Optimisation')) return;
    if (pointType !== 'pdl') {
      toast.error("Optimisation : seulement PDL (electricite) pour l'instant.");
      return;
    }
    const pdl = (pdls || []).find(p => p.id === pointId);
    if (!pdl) { toast.error('PDL introuvable.'); return; }

    const key = pointId + '-' + yearToAnalyze;
    setOptimizingStates(prev => ({ ...prev, [key]: true }));
    const toastId = toast.loading('Etape 1/3 : Lancement du calcul TURPE...');

    try {
      await fetch('/api/v3/calculate-from-pdl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pdl_number: pdl.pdl_number, user_id: userId, analysis_year: yearToAnalyze }),
      }).then(res => lireReponseApi(res, 'Calcul TURPE'))
        .then(corps => (Array.isArray(corps?.warnings) ? corps.warnings : [])
          .forEach(message => toast(message, { icon: '⚠️', duration: 12000 })));

      toast.loading('Etape 2/3 : Lancement des optimisations...', { id: toastId });

      const requestBody = { pdl_number: pdl.pdl_number, user_id: userId, analysis_year: yearToAnalyze };
      const versionFormData = new FormData();
      versionFormData.append('pdl_number', pdl.pdl_number);
      versionFormData.append('user_id', userId);
      versionFormData.append('analysis_year', String(yearToAnalyze));

      const [psResult, versionResult] = await Promise.all([
        axios.post('/api/v4/optimize-contract-smart',
          { ...requestBody, risk_tolerance: 'balanced' },
          { headers: authHeaders }),
        axios.post('/api/v4/analyze-version-utilisation',
          versionFormData,
          { headers: authHeaders }),
      ]);

      toast.loading('Etape 3/3 : Sauvegarde des resultats...', { id: toastId });

      const psDataToSave = psResult.data;
      const rawVersionResult = versionResult.data;
      const versionDataToSave = versionPourSauvegarde(rawVersionResult);

      // Backend attend `targetAccountId` (cf. turpe.controller.js#updateAnalysisResult).
      // Sans ça, finalAccountId tombe sur userAccountId (= compte de l'auditeur qui
      // lance l'analyse) et la row est sauvée sous le mauvais account → le refresh
      // de ClientDetailPage cherche par accountId du target et ne trouve rien →
      // le bouton "Détails" ne réapparaît jamais.
      await Promise.all([
        axios.post('/api/turpe/update-analysis-result', {
          pdl_id: pointId, targetAccountId: accountId,
          analysis_type: 'auto_optimization', result_data: psDataToSave,
          analysis_year: yearToAnalyze,
        }, { headers: authHeaders }),
        axios.post('/api/turpe/update-analysis-result', {
          pdl_id: pointId, targetAccountId: accountId,
          analysis_type: 'version_of_use', result_data: versionDataToSave,
          analysis_year: yearToAnalyze,
        }, { headers: authHeaders }),
      ]);

      toast.success('Analyses et optimisations terminees !', { id: toastId });
      if (onRefresh) await onRefresh();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message;
      toast.error('Erreur: ' + errorMessage, { id: toastId });
    } finally {
      setOptimizingStates(prev => ({ ...prev, [key]: false }));
    }
  }, [requireMandat, pdls, session?.access_token, userId, accountId, onRefresh, authHeaders]);

  // ---------- DELETE DOCUMENT ----------
  const handleDeleteDocument = async (docToDelete) => {
    if (!requireMandat('Suppression document')) return;
    if (!window.confirm('Supprimer le fichier "' + docToDelete.name + '" ?')) return;

    const toastId = toast.loading('Suppression en cours...');
    let apiUrl = '';
    switch (docToDelete.docType) {
      case 'courbe_charge': apiUrl = '/api/enedis/courbe-charge/uploads/' + docToDelete.id; break;
      case 'historique_consommation': apiUrl = '/api/enedis/historique-consommations/uploads/' + docToDelete.id; break;
      case 'infos_techniques_contractuelles':
        if (!docToDelete.pdlId) { toast.error("PDL ID manquant.", { id: toastId }); return; }
        apiUrl = '/api/enedis/infos-techniques/' + docToDelete.pdlId; break;
      case 'donnees_techniques_soutirage': apiUrl = '/api/enedis/donnees-techniques/uploads/' + docToDelete.id; break;
      default: toast.error('Type inconnu.', { id: toastId }); return;
    }
    try {
      await axios.delete(apiUrl, { headers: authHeaders });
      toast.success('Fichier supprime.', { id: toastId });
      await loadAcheminementData();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'La suppression a echoue.', { id: toastId });
    }
  };

  // ---------- STATUS HELPERS ----------

  /**
   * Retourne le fichier le plus récent reçu pour ce PDL et ce docType (toutes années
   * confondues). Renvoie null si aucun fichier.
   */
  const getLatestFileForType = (pointId, docType) => {
    const matches = (documents || []).filter(d => {
      const idMatch = (d.pdlId && d.pdlId === pointId) || (d.pceId && d.pceId === pointId);
      return idMatch && d.docType === docType;
    });
    if (matches.length === 0) return null;
    matches.sort((a, b) => {
      const aT = new Date(a.uploadedAt || a.created_at || 0).getTime();
      const bT = new Date(b.uploadedAt || b.created_at || 0).getTime();
      return bT - aT;
    });
    return matches[0];
  };

  /**
   * Renvoie pour un point le statut des 3 fichiers ENEDIS attendus (C68/R65/R67).
   * Optimiser dispo si les 3 sont reçus.
   */
  const getPointEnedisStatus = (pointId) => {
    const c68 = getLatestFileForType(pointId, 'infos_techniques_contractuelles');
    const r65 = getLatestFileForType(pointId, 'courbe_charge');
    const r63 = getLatestFileForType(pointId, 'courbe_charge_30min');
    const r67 = getLatestFileForType(pointId, 'historique_consommation');
    // R63 reste optionnel pour le calcul "complet" : sur les PDL C4 (>36 kVA),
    // ENEDIS ne sert pas R63 via F300C (la CdC passe par d'autres flux R151/R171/
    // R172). Donc "Optimiser" se déverrouille dès que C68+R65+R67 sont là.
    const filesReceived = [c68, r65, r67].filter(Boolean).length;
    return { c68, r65, r63, r67, filesReceived, isComplete: filesReceived === 3 };
  };

  /**
   * Détermine le docType à partir du nom de fichier ENEDIS standard.
   * Ex: ENEDIS_C68_P_ITC_*.zip → infos_techniques_contractuelles
   *     ENEDIS_R65_P_Energie_*.zip → courbe_charge
   *     ENEDIS_R67_P_MFact_*.zip → historique_consommation
   */
  const detectDocTypeFromFilename = (filename) => {
    const name = (filename || '').toUpperCase();
    if (/ENEDIS[_\-]?C68/.test(name)) return 'infos_techniques_contractuelles';
    if (/ENEDIS[_\-]?R(63|65)/.test(name)) return 'courbe_charge';
    if (/ENEDIS[_\-]?R67/.test(name)) return 'historique_consommation';
    return null;
  };

  // ---------- ZIP MULTI-UPLOAD HANDLERS ----------
  const openZipModal = (point) => {
    setZipModalPoint(point);
    setShowZipModal(true);
  };

  const closeZipModal = () => {
    if (zipImporting) return;
    setShowZipModal(false);
    setZipModalPoint(null);
  };

  /**
   * Reçoit une FileList (drop ou input multiple), classifie chaque ZIP par nom,
   * et dispatche vers les handlers existants. L'année est forcée à l'année courante
   * (les flux ENEDIS Data Connect sont continus, le slot année est conservé pour
   * compatibilité backend uniquement).
   */
  const handleZipMultiUpload = async (files) => {
    if (!zipModalPoint) return;
    if (zipModalPoint.type !== 'pdl') {
      toast.error("Upload uniquement disponible pour les PDL.");
      return;
    }
    const list = Array.from(files || []);
    if (list.length === 0) return;
    if (!requireMandat('Import ZIP ENEDIS')) return;

    setZipImporting(true);
    const year = (new Date().getFullYear() - 1);
    const summary = { ok: 0, ko: 0, unknown: 0 };

    for (const file of list) {
      const docType = detectDocTypeFromFilename(file.name);
      if (!docType) {
        summary.unknown += 1;
        toast.error(`"${file.name}" : type ENEDIS non reconnu (attendu C68 / R63 / R65 / R67).`);
        continue;
      }
      try {
        if (docType === 'courbe_charge')                     await handleCourbeChargeUpload(file, zipModalPoint.point_id, year);
        else if (docType === 'historique_consommation')      await handleHistoriqueConsommationsUpload(file, zipModalPoint.point_id, year);
        else if (docType === 'infos_techniques_contractuelles') await handleInfosTechniquesUpload(file, zipModalPoint.point_id, year);
        summary.ok += 1;
      } catch (e) {
        summary.ko += 1;
      }
    }

    setZipImporting(false);
    if (summary.ok > 0 || summary.ko > 0) {
      toast.success(`Import terminé : ${summary.ok} ok${summary.ko ? `, ${summary.ko} échec(s)` : ''}${summary.unknown ? `, ${summary.unknown} ignoré(s)` : ''}.`);
    }
    if (summary.ko === 0 && summary.unknown === 0) {
      setShowZipModal(false);
      setZipModalPoint(null);
    }
  };

  // ---------- YEAR-BASED HELPER (legacy, gardé temporairement pour ne pas casser ailleurs) ----------
  const getPointStatusForYear = (pointId, year) => {
    const requiredDocs = ACHEMINEMENT_DOCS.filter(d => d.required);
    const totalRequired = requiredDocs.length;
    const docsForPointYear = documents.filter(d => {
      const idMatch = (d.pdlId && d.pdlId === pointId) || (d.pceId && d.pceId === pointId);
      return idMatch && d.year === year;
    });
    const uploadedRequiredDocs = new Set(
      docsForPointYear.filter(d => requiredDocs.some(req => req.id === d.docType)).map(d => d.docType)
    );
    const count = uploadedRequiredDocs.size;
    let status = 'a fournir';
    if (count > 0 && count < totalRequired) status = 'partiel';
    if (count >= totalRequired) status = 'complet';
    return { count, total: totalRequired, status };
  };

  const navigateToAnalysis = (pdlId, year, analysisResult) => {
    const pdl = (pdls || []).find(p => p.id === pdlId);
    navigate('/dashboard/admin/analyse-turpe/' + pdlId, {
      state: { pdl, year, analysisResultsByPdl: analysisResult },
    });
  };

  // ============================================================================
  // UPLOAD MODAL (unchanged UI)
  // ============================================================================
  const UploadModal = () => {
    if (!showUploadModal || !selectedPointId || !selectedYear) return null;
    const point = deliveryPoints.find(p => p.point_id === selectedPointId);
    const isPdl = selectedPointType === 'pdl';
    const docsForPointYear = documents.filter(d => {
      const idMatch = isPdl ? (d.pdlId === selectedPointId) : (d.pceId === selectedPointId);
      return idMatch && d.year === selectedYear;
    });
    const callUploadHandler = (docType, file) => {
      if (!isPdl) { toast.error("Upload uniquement PDL pour l'instant."); return; }
      switch (docType) {
        case 'courbe_charge': return handleCourbeChargeUpload(file, selectedPointId, selectedYear);
        case 'historique_consommation': return handleHistoriqueConsommationsUpload(file, selectedPointId, selectedYear);
        case 'infos_techniques_contractuelles': return handleInfosTechniquesUpload(file, selectedPointId, selectedYear);
        case 'donnees_techniques_soutirage': return handleSoutirageUpload(file, selectedPointId, selectedYear);
        default: toast.error("Type non reconnu.");
      }
    };

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2">
        <div className="bg-white rounded-lg shadow-2xl p-3 max-w-md w-full border border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-xs font-semibold text-gray-800">Upload Documents Acheminement</h3>
            <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600 text-sm px-1">
              <FiX className="w-4 h-4" />
            </button>
          </div>
          <div className="mb-2 p-2 bg-purple-50 border border-purple-200 rounded-md">
            <p className="text-[10px] font-semibold text-purple-800 truncate">{point?.site_name || 'Site'}</p>
            <p className="text-[9px] text-purple-700">
              {selectedPointType?.toUpperCase()} - Annee fiscale : <span className="font-semibold">{selectedYear}</span>
            </p>
          </div>
          {!isPdl && (
            <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-[10px] font-semibold text-yellow-900">PCE (Gaz) : upload non disponible</p>
            </div>
          )}
          <div className={!isPdl ? 'opacity-60 pointer-events-none' : 'space-y-2'}>
            {ACHEMINEMENT_DOCS.map(doc => {
              const uploadedFiles = docsForPointYear.filter(d => d.docType === doc.id);
              const isUploaded = uploadedFiles.length > 0;
              let isLoadingThisType = false;
              if (doc.id === 'courbe_charge') isLoadingThisType = isUploadingCourbeCharge;
              if (doc.id === 'historique_consommation') isLoadingThisType = isUploadingHistorique;
              if (doc.id === 'infos_techniques_contractuelles') isLoadingThisType = isUploadingInfosTechniques;
              if (doc.id === 'donnees_techniques_soutirage') isLoadingThisType = isUploadingSoutirage;
              return (
                <div key={doc.id} className={'p-2 rounded-md border transition-colors ' + (isUploaded ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200')}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {isUploaded && <FiCheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
                      <span className="text-[10px] font-semibold text-gray-800 truncate">{doc.name}</span>
                      {!doc.required && <span className="text-[9px] text-gray-500">(Opt.)</span>}
                    </div>
                    {isLoadingThisType ? (
                      <FiLoader className="animate-spin text-purple-500 w-4 h-4" />
                    ) : (
                      <label htmlFor={doc.id + '-upload'} className={'px-2 py-1 text-[10px] font-semibold rounded-md cursor-pointer ' + (isUploaded ? 'bg-green-100 hover:bg-green-200 text-green-800' : 'bg-purple-100 hover:bg-purple-200 text-purple-800')}>
                        {isUploaded ? 'Remplacer' : 'Upload'}
                      </label>
                    )}
                    <input id={doc.id + '-upload'} type="file" className="hidden" onChange={(e) => { if (e.target.files?.[0]) callUploadHandler(doc.id, e.target.files[0]); }} disabled={isLoadingThisType} />
                  </div>
                  {isUploaded && (
                    <ul className="mt-2 pt-2 border-t border-green-200 space-y-1.5">
                      {uploadedFiles.map(file => (
                        <li key={file.id} className="flex items-center justify-between gap-2 bg-white px-2 py-1.5 rounded border border-gray-100">
                          <span className="truncate text-gray-700 font-mono text-[9px]" title={file.name}>{file.name}</span>
                          <button onClick={() => handleDeleteDocument(file)} className="w-6 h-6 inline-flex items-center justify-center rounded bg-red-50 text-red-600 hover:bg-red-100" title="Supprimer">
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
            {uploadProgress > 0 && <div className="text-[10px] text-gray-500">Upload : {uploadProgress}%</div>}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // ZIP MULTI-UPLOAD MODAL (mode secours, 3 ZIPs ENEDIS C68/R65/R67)
  // ============================================================================
  const ZipDropZoneModal = () => {
    if (!showZipModal || !zipModalPoint) return null;
    const enedis = getPointEnedisStatus(zipModalPoint.point_id);

    const onPickFiles = () => zipInputRef.current?.click();
    const onFiles = async (files) => {
      await handleZipMultiUpload(files);
    };
    const onDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (zipImporting) return;
      onFiles(e.dataTransfer?.files);
    };
    const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
        <div className="bg-white rounded-lg shadow-2xl p-4 max-w-lg w-full border border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-xs font-semibold text-gray-800">Importer fichiers ENEDIS — mode secours</h3>
            <button onClick={closeZipModal} disabled={zipImporting} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
              <FiX className="w-4 h-4" />
            </button>
          </div>

          <div className="mb-3 p-2 bg-purple-50 border border-purple-200 rounded-md">
            <p className="text-[10px] font-semibold text-purple-800 truncate">{zipModalPoint.site_name}</p>
            <p className="text-[9px] text-purple-700 font-mono">{(zipModalPoint.type || '').toUpperCase()} — {zipModalPoint.point_number || '—'}</p>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-3">
            {[
              { label: 'C68 — Techniques', file: enedis.c68 },
              { label: 'R65 — Courbe',     file: enedis.r65 },
              { label: 'R67 — Facturant',  file: enedis.r67 },
            ].map(s => (
              <div key={s.label} className={'rounded-md border p-2 text-center ' + (s.file ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200')}>
                <div className="text-[9px] font-semibold text-gray-700 mb-1">{s.label}</div>
                {s.file ? (
                  <span className="inline-flex items-center gap-1 text-[9px] font-medium text-emerald-700">
                    <FiCheckCircle className="w-2.5 h-2.5" /> Reçu
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[9px] font-medium text-gray-500">
                    <FiAlertCircle className="w-2.5 h-2.5" /> À fournir
                  </span>
                )}
              </div>
            ))}
          </div>

          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onClick={zipImporting ? undefined : onPickFiles}
            className={'rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ' + (zipImporting ? 'opacity-60 cursor-wait border-gray-200' : 'border-purple-300 hover:bg-purple-50')}
          >
            {zipImporting ? (
              <div className="flex flex-col items-center gap-2">
                <FiLoader className="w-6 h-6 text-purple-500 animate-spin" />
                <p className="text-[10px] text-gray-700">Import en cours…</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <FiUploadCloud className="w-7 h-7 text-purple-500" />
                <p className="text-[10px] font-semibold text-gray-800">Glissez les ZIPs ENEDIS ou cliquez pour parcourir</p>
                <p className="text-[9px] text-gray-500">Auto-classification par nom de fichier (ENEDIS_C68_*, ENEDIS_R65_*, ENEDIS_R67_*).</p>
              </div>
            )}
            <input
              ref={zipInputRef}
              type="file"
              multiple
              accept=".zip,application/zip,application/x-zip-compressed"
              className="hidden"
              onChange={(e) => onFiles(e.target.files)}
            />
          </div>

          <div className="mt-3 text-[9px] text-gray-500">
            Conseil : sélectionne les 3 fichiers en une fois (Cmd/Ctrl+clic). Le système les répartit automatiquement.
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SFTP DETAIL DRAWER (reads via Node endpoint, NOT Supabase direct)
  // ============================================================================
  const SftpDrawer = () => {
    if (!showSftpDrawer) return null;
    return (
      <div className="fixed inset-0 bg-black/40 flex justify-end z-50">
        <div className="bg-white w-full max-w-md h-full shadow-xl overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <FiList className="w-4 h-4 text-purple-500" />
              Fichiers SFTP
            </h3>
            <div className="flex items-center gap-2">
              <button onClick={() => loadSftpStatus(sftpDrawerPdlId)} className="text-[10px] text-purple-600 hover:text-purple-800 flex items-center gap-1">
                <FiRefreshCw className="w-3 h-3" /> Actualiser
              </button>
              <button onClick={() => setShowSftpDrawer(false)} className="text-gray-400 hover:text-gray-600">
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="p-4">
            {sftpLoading ? (
              <div className="flex items-center justify-center py-8">
                <FiLoader className="animate-spin h-5 w-5 text-purple-500 mr-2" />
                <span className="text-xs text-gray-500">Chargement...</span>
              </div>
            ) : sftpFiles.length === 0 ? (
              <div className="text-center py-8">
                <FiFileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-500">Aucun fichier SFTP pour ce PDL.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sftpFiles.map(f => {
                  const badge = mapSftpStatusBadge(f.status);
                  return (
                    <div key={f.id} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-mono text-gray-800 truncate" title={f.filename}>{f.filename}</p>
                          <p className="text-[9px] text-gray-500 mt-0.5">
                            {f.file_type || '—'} - {formatDateParis(f.created_at)}
                          </p>
                          {f.parsed_at && (
                            <p className="text-[9px] text-green-600 mt-0.5">Parse : {formatDateParis(f.parsed_at)}</p>
                          )}
                          {f.parsed_error && (
                            <p className="text-[9px] text-red-600 mt-0.5 truncate" title={f.parsed_error}>Erreur: {f.parsed_error}</p>
                          )}
                        </div>
                        <span className={'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold ' + badge.cls}>
                          {badge.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // LOADING
  // ============================================================================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <FiLoader className="animate-spin h-5 w-5 text-purple-500 mr-2" />
        <span className="text-[10px] text-gray-600">Chargement...</span>
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================
  const firstPdl = pdls[0];
  const StatusBadge = mapStatusToBadge(enedisStatus);
  const StatusIcon = StatusBadge.icon;

  return (
    <div className="space-y-2">
      {/* ================================================================ */}
      {/* HEADER */}
      {/* ================================================================ */}
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg px-3 py-2 text-white">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FiZap className="h-5 w-5 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-sm font-bold leading-tight">Gestion Acheminement</h1>
              <p className="text-[10px] text-purple-100 leading-tight truncate">
                Optimisation des couts d'acheminement pour {clientData?.name}
              </p>
            </div>
          </div>
          <button onClick={handleRefresh} disabled={isRefreshing} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-white/20 hover:bg-white/30 rounded-md disabled:opacity-50 transition-colors">
            {isRefreshing ? <FiLoader className="h-3.5 w-3.5 animate-spin" /> : <FiRefreshCw className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">{isRefreshing ? 'En cours...' : 'Actualiser'}</span>
          </button>
        </div>
      </div>

      {/* ================================================================ */}
      {/* BLOC A: SYNCHRONISATION ENEDIS (recommandee) — PRINCIPAL         */}
      {/* ================================================================ */}
      <div data-pdl={firstPdl?.pdl_number} className="bg-white rounded-lg border border-purple-200 overflow-hidden">
        <div className="px-3 py-2 bg-purple-50 border-b border-purple-200">
          <h2 className="text-xs font-semibold text-purple-900 flex items-center gap-1.5">
            <FiLink className="w-3.5 h-3.5 text-purple-600" />
            Synchronisation Enedis (recommandee)
          </h2>
          <p className="text-[9px] text-purple-700 mt-0.5">
            Connexion directe aux donnees Enedis via mandat electronique. Les fichiers sont recuperes automatiquement.
          </p>
        </div>

        <div className="p-3 space-y-3">
          {/* --- A1: Etat Mandat (account-level) --- */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              {mandateOk ? (
                <div className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center">
                  <FiShield className="h-4 w-4 text-green-600" />
                </div>
              ) : (
                <div className="h-7 w-7 rounded-full bg-red-100 flex items-center justify-center">
                  <FiShield className="h-4 w-4 text-red-500" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-gray-900">Mandat ENEDIS</span>
                {mandateOk ? (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-green-100 text-green-800">
                    <FiCheckCircle className="mr-0.5 h-3 w-3" /> Signe le {formatDateParis(mandateSignedAt)}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-red-100 text-red-800">
                    <FiAlertCircle className="mr-0.5 h-3 w-3" /> Requis
                  </span>
                )}
              </div>
              {!mandateOk && (
                <p className="text-[9px] text-red-700 mt-0.5">
                  Mandat requis (signature electronique).{' '}
                  <span data-cta="sign-mandat" className="font-semibold text-red-800 underline cursor-pointer" onClick={() => toast('Generez le mandat dans le CRM et faites-le signer via Yousign.')}>
                    Signer le mandat
                  </span>
                </p>
              )}
              {mandateOk && signedMandatPublicUrl && (
                <a href={signedMandatPublicUrl} onClick={(e) => { e.preventDefault(); ouvrirDocumentStocke(signedMandatPublicUrl); }} target="_blank" rel="noopener noreferrer" className="text-[9px] text-blue-600 hover:underline flex items-center gap-1 mt-0.5">
                  <FiEye className="w-3 h-3" /> Voir le mandat
                </a>
              )}
            </div>
          </div>

          {/* --- A2: Etat Connexion Enedis --- */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <div className={'h-7 w-7 rounded-full flex items-center justify-center ' + (enedisStatus === 'CONNECTED' ? 'bg-green-100' : 'bg-gray-100')}>
                <StatusIcon className={'h-4 w-4 ' + (enedisStatus === 'CONNECTED' ? 'text-green-600' : 'text-gray-500')} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-semibold text-gray-900">Connexion Enedis</span>
                <span className={'inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold ' + StatusBadge.color}>
                  {StatusBadge.label}
                </span>
              </div>

              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {enedisStatus === 'NOT_CONNECTED' || enedisStatus === 'ERROR' ? (
                  <button
                    data-cta="connect-enedis"
                    onClick={() => firstPdl && handleConnectEnedis(firstPdl.id)}
                    disabled={!mandateOk || isConnecting || !firstPdl}
                    className={'inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold rounded-md transition-colors ' +
                      (mandateOk && firstPdl
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed')}
                    title={!mandateOk ? 'Mandat requis' : !firstPdl ? 'Aucun PDL' : ''}
                  >
                    {isConnecting ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiLink className="w-3 h-3" />}
                    Connecter a Enedis
                  </button>
                ) : enedisStatus === 'PENDING' ? (
                  <div className="flex items-center gap-1 text-[10px] text-yellow-700">
                    <FiLoader className="w-3 h-3 animate-spin" />
                    Synchronisation en cours...
                  </div>
                ) : (
                  <button
                    onClick={() => firstPdl && handleConnectEnedis(firstPdl.id)}
                    disabled={isConnecting}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold rounded-md bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
                  >
                    <FiRefreshCw className="w-3 h-3" />
                    Synchroniser maintenant
                  </button>
                )}

                {firstPdl && (
                  <button
                    onClick={() => openSftpDrawer(firstPdl.id)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md border border-gray-200 transition-colors"
                  >
                    <FiEye className="w-3 h-3" />
                    Voir le detail
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* BLOC B: FALLBACK — Importer un fichier (mode secours) Accordion  */}
      {/* ================================================================ */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <button
          onClick={() => setFallbackOpen(!fallbackOpen)}
          className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-1.5">
            <FiEdit3 className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-semibold text-gray-700">Importer un fichier (mode secours)</span>
          </div>
          {fallbackOpen ? <FiChevronDown className="w-4 h-4 text-gray-400" /> : <FiChevronRight className="w-4 h-4 text-gray-400" />}
        </button>

        {fallbackOpen && (
          <div className="border-t border-gray-200">
            {/* Warning if synchro is connected */}
            {enedisStatus === 'CONNECTED' && (
              <div className="px-3 py-2 bg-yellow-50 border-b border-yellow-200">
                <div className="flex items-start gap-2">
                  <FiAlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] text-yellow-800">
                    <span className="font-semibold">Synchro active</span> — import manuel generalement inutile. Les donnees sont recuperees automatiquement.
                  </p>
                </div>
              </div>
            )}

            <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200">
              <p className="text-[9px] text-gray-500">
                A utiliser uniquement si la synchronisation Enedis est indisponible ou pour un import exceptionnel.
              </p>
            </div>

            {/* 3-slot table : C68 / R65 / R67 — bloqué si pas de mandat */}
            <div className={!hasMandat ? 'opacity-50 pointer-events-none select-none' : ''}>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-[9px] font-semibold text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-[220px]">Point</th>
                      <th className="px-2 py-2 text-center text-[9px] font-semibold text-gray-500 uppercase tracking-wider min-w-[140px]">C68 — Techniques</th>
                      <th className="px-2 py-2 text-center text-[9px] font-semibold text-gray-500 uppercase tracking-wider min-w-[140px]">R65 — Conso jour.</th>
                      <th className="px-2 py-2 text-center text-[9px] font-semibold text-gray-500 uppercase tracking-wider min-w-[140px]">R63 — CdC 30 min</th>
                      <th className="px-2 py-2 text-center text-[9px] font-semibold text-gray-500 uppercase tracking-wider min-w-[140px]">R67 — Facturant</th>
                      <th className="px-2 py-2 text-right text-[9px] font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {deliveryPoints.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-6">
                          <FiZap className="h-7 w-7 mx-auto text-gray-400 mb-2" />
                          <h3 className="text-xs font-semibold text-gray-900 mb-1">Aucun PDL/PCE</h3>
                          <p className="text-[10px] text-gray-600">Creez des points de livraison dans l'onglet general.</p>
                        </td>
                      </tr>
                    ) : (
                      deliveryPoints.map((point, index) => {
                        const enedis = getPointEnedisStatus(point.point_id);
                        const optKey = point.point_id + '-' + (new Date().getFullYear() - 1);
                        const optimizationResult = analysisResults[optKey];
                        const isOptimizing = !!optimizingStates[optKey];
                        const canOptimize = enedis.isComplete && point.type === 'pdl';
                        const isPdl = point.type === 'pdl';

                        // Loading flag par docType
                        const isLoadingFor = (docType) => {
                          if (docType === 'courbe_charge') return isUploadingCourbeCharge;
                          if (docType === 'historique_consommation') return isUploadingHistorique;
                          if (docType === 'infos_techniques_contractuelles') return isUploadingInfosTechniques;
                          return false;
                        };

                        // Dispatch upload pour un docType précis
                        const uploadOne = (docType, file) => {
                          if (!file) return;
                          if (!isPdl) { toast.error('Upload uniquement disponible pour les PDL.'); return; }
                          if (!requireMandat('Import')) return;
                          const year = (new Date().getFullYear() - 1);
                          if (docType === 'courbe_charge')                        handleCourbeChargeUpload(file, point.point_id, year);
                          else if (docType === 'historique_consommation')         handleHistoriqueConsommationsUpload(file, point.point_id, year);
                          else if (docType === 'infos_techniques_contractuelles') handleInfosTechniquesUpload(file, point.point_id, year);
                        };

                        // Slot interactif : clic = sélectionner le fichier ZIP pour ce docType précis
                        const SlotBadge = ({ file, label, docType }) => {
                          const inputId = 'slot-' + point.point_id + '-' + docType;
                          const loading = isLoadingFor(docType);
                          // Stub synthétique (source='enedis-native') : fichier ingéré via SFTP/
                          // Data Connect/script, on n'a pas de date côté `enedis_*` (juste un
                          // EXISTS via RPC enedis_pdl_files_status). On affiche "(synchro)"
                          // plutôt qu'une date absente.
                          const isSynthetic = file?.source === 'enedis-native';
                          const rawDate = file ? formatDateParis(file.uploadedAt || file.created_at) : null;
                          const hasRealDate = rawDate && rawDate !== '—';

                          const baseCls = 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium ring-1 ring-inset cursor-pointer transition-colors';
                          const stateCls = file
                            ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100'
                            : 'bg-red-50 text-red-700 ring-red-200 hover:bg-red-100';
                          const titleAttr = file
                            ? (isSynthetic
                                ? `${label} reçu via synchro ENEDIS : ${file.name}\nClic = remplacer manuellement`
                                : `${label} reçu${hasRealDate ? ' le ' + rawDate : ''} : ${file.name}\nClic = remplacer`)
                            : `Cliquer pour importer le ZIP ${label}`;

                          return (
                            <>
                              <label
                                htmlFor={inputId}
                                className={baseCls + ' ' + stateCls + (loading ? ' opacity-60 cursor-wait' : '')}
                                title={titleAttr}
                              >
                                {loading ? (
                                  <FiLoader className="w-2.5 h-2.5 animate-spin" />
                                ) : file ? (
                                  <FiCheckCircle className="w-2.5 h-2.5" />
                                ) : (
                                  <FiAlertCircle className="w-2.5 h-2.5" />
                                )}
                                {file
                                  ? (isSynthetic ? 'Reçu (synchro)' : (hasRealDate ? `Reçu ${rawDate.slice(0, 10)}` : 'Reçu'))
                                  : 'À fournir'}
                              </label>
                              <input
                                id={inputId}
                                type="file"
                                accept=".zip,application/zip,application/x-zip-compressed"
                                className="hidden"
                                disabled={loading || !isPdl}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  uploadOne(docType, f);
                                  e.target.value = '';
                                }}
                              />
                            </>
                          );
                        };

                        return (
                          <tr key={point.type + '-' + point.point_id} data-pdl={point.type === 'pdl' ? point.point_number : undefined} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 py-2 whitespace-nowrap sticky left-0 z-10 border-r border-gray-200 bg-inherit">
                              <div className="flex items-start">
                                <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                                  <FiZap className="h-3.5 w-3.5 text-purple-600" />
                                </div>
                                <div className="ml-2 min-w-0">
                                  <div className="text-[10px] font-semibold text-gray-900 leading-tight truncate">{point.site_name}</div>
                                  <div className="text-[9px] text-gray-500 font-mono leading-tight truncate">{point.type.toUpperCase()} - {point.point_number || '—'}</div>
                                  <div className="text-[9px] text-gray-400 flex items-center gap-1 leading-tight">
                                    <FiMapPin className="w-3 h-3" />
                                    <span className="truncate">{point.address?.city || '—'}{point.contract_power ? ' - ' + point.contract_power + ' kVA' : ''}</span>
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="px-2 py-2 text-center align-middle"><SlotBadge file={enedis.c68} label="C68" docType="infos_techniques_contractuelles" /></td>
                            <td className="px-2 py-2 text-center align-middle"><SlotBadge file={enedis.r65} label="R65" docType="courbe_charge" /></td>
                            <td className="px-2 py-2 text-center align-middle">
                              {/* R63 (CdC 30 min) : affichage read-only. Pas d'upload manuel via
                                  cette UI — R63 vient de la synchro ENEDIS ou de l'ingestion ZIP.
                                  Sur C4 (>36 kVA), R63 n'est généralement pas servi via F300C. */}
                              <span
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium ring-1 ring-inset ${
                                  enedis.r63
                                    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                                    : 'bg-gray-50 text-gray-500 ring-gray-200'
                                }`}
                                title={enedis.r63
                                  ? `R63 (CdC) reçu via synchro ENEDIS${enedis.r63.created_at ? ' le ' + formatDateParis(enedis.r63.created_at) : ''}`
                                  : 'Optionnel — la CdC 30 min n\'est servie par ENEDIS (F300C) que pour le segment C5 (≤ 36 kVA Linky).'}
                              >
                                {enedis.r63
                                  ? <><FiCheckCircle className="w-2.5 h-2.5" /> Reçu</>
                                  : <>—</>}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-center align-middle"><SlotBadge file={enedis.r67} label="R67" docType="historique_consommation" /></td>

                            <td className="px-2 py-2 align-middle">
                              {optimizationResult ? (
                                <div className="flex flex-col items-end gap-1">
                                  <div className="text-[12px] font-bold text-green-600 leading-tight">
                                    {(optimizationResult.best_saving_eur)?.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button onClick={() => navigateToAnalysis(point.point_id, (new Date().getFullYear() - 1), optimizationResult)} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-md">
                                      <FiExternalLink className="w-3 h-3" /> Détails
                                    </button>
                                    {/* Relancer : ré-exécute l'analyse (upsert par pdl_id+analysis_year)
                                        pour intégrer de nouvelles données ou re-tarifer après un fix. */}
                                    {point.type === 'pdl' && (
                                      isOptimizing ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] text-purple-600">
                                          <FiLoader className="animate-spin w-3 h-3" />
                                        </span>
                                      ) : (
                                        <button
                                          onClick={() => handleLaunchAnalysis(point.point_id, (new Date().getFullYear() - 1), point.type)}
                                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200"
                                          title="Relancer l'analyse TURPE (écrase le résultat précédent)"
                                        >
                                          <FiRefreshCw className="w-3 h-3" /> Relancer
                                        </button>
                                      )
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center justify-end gap-1.5">
                                  {point.type === 'pdl' && (
                                    <button
                                      onClick={() => openZipModal(point)}
                                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md bg-purple-100 text-purple-800 hover:bg-purple-200"
                                    >
                                      <FiUploadCloud className="w-3 h-3" /> Importer ZIPs
                                    </button>
                                  )}
                                  {isOptimizing ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] text-purple-600">
                                      <FiLoader className="animate-spin w-3 h-3" /> Analyse…
                                    </span>
                                  ) : (
                                    <button
                                      data-cta="optimize"
                                      onClick={() => {
                                        if (!canOptimize) return;
                                        // Calcul inline (toasts + résultat affiché dans la cellule
                                        // via optimizationResult). Ensuite, bouton "Détails" qui apparaît
                                        // permet d'ouvrir la page AnalyseTurpe.
                                        handleLaunchAnalysis(point.point_id, (new Date().getFullYear() - 1), point.type);
                                      }}
                                      disabled={!canOptimize}
                                      className={'inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md transition-colors ' + (canOptimize ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed')}
                                      title={canOptimize ? 'Lancer l\'optimisation TURPE' : 'Disponible quand les 3 fichiers sont reçus'}
                                    >
                                      <FiPlay className="w-3 h-3" /> Optimiser
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <UploadModal />
      <ZipDropZoneModal />
      <SftpDrawer />
    </div>
  );
};

export default AcheminementDetail;
